import {
  useEffect,
  useRef,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type PropsWithChildren,
  type SubmitEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { isOrvalError, isValidationErrorDetail } from "@/compat";
import { multiref } from "@/multiref";

function findValidationField(form: HTMLFormElement, path: string) {
  const byValidationName = form.querySelector(`[data-validation-name="${CSS.escape(path)}"]`);
  if (byValidationName instanceof HTMLInputElement || byValidationName instanceof HTMLTextAreaElement) {
    return byValidationName;
  }
  const field = form.elements.namedItem(path);
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
    return field;
  }
  return null;
}

export function Form({
  error,
  children,
  onSubmit,
  ref,
  ...props
}: {
  error?: unknown;
} & PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLFormElement>, HTMLFormElement>>) {
  const { t } = useTranslation("errors");
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!isOrvalError(error) || !isValidationErrorDetail(error.info)) return;
    if (!form.current) return;
    for (const fieldError of error.info.detail.field_errors) {
      const field = findValidationField(form.current, fieldError.path.join("."));
      if (!field) continue;
      field.setCustomValidity(
        t(fieldError.code, { defaultValue: fieldError.message, ...(fieldError.detail as Record<string, unknown>) }),
      );
    }
    form.current.reportValidity();
  }, [error, t]);

  function resetValidity() {
    for (const field of Array.from(form.current!.elements)) {
      if (field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement) {
        field.setCustomValidity("");
      }
    }
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit?.(event);
  }

  return (
    <form onSubmit={handleSubmit} ref={multiref(form, ref)} {...props} onInput={() => resetValidity()}>
      {children}
    </form>
  );
}
